/**
 * Container for a single curated section.
 *
 * Builds per-player press handlers, drops empty sections to avoid
 * empty-header flashes, and delegates rendering to the view.
 * @module features/global-search/components/GlobalSearchCuratedSections/CuratedSection/CuratedSectionContainer
 */

import React, { memo, useMemo } from "react";

import { type PlayerSearchPlayerFragment } from "@/generated/graphql";

import CuratedSectionView from "./CuratedSectionView";

/** Props for the CuratedSectionContainer */
interface CuratedSectionContainerProps {
  /** Section heading text */
  readonly title: string;
  /** Players to render */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether the section's initial query is still loading */
  readonly loading: boolean;
  /** Row press handler */
  readonly onPlayerPress: (player: PlayerSearchPlayerFragment) => void;
  /** Action slot renderer per row */
  readonly renderAction: (
    player: PlayerSearchPlayerFragment
  ) => React.ReactNode;
  /** Active sort field for the metric badge */
  readonly effectiveSortBy: string;
  /** Display label for the sort field */
  readonly sortFieldLabel: string;
}

/**
 * Builds stable per-player press handlers and routes to the view
 * @param root0 - Component props
 * @param root0.title - Section heading
 * @param root0.players - Players to render
 * @param root0.loading - Whether the initial query is loading
 * @param root0.onPlayerPress - Row press handler
 * @param root0.renderAction - Action slot renderer
 * @param root0.effectiveSortBy - Active sort field
 * @param root0.sortFieldLabel - Sort field display label
 */
const CuratedSectionContainer = ({
  title,
  players,
  loading,
  onPlayerPress,
  renderAction,
  effectiveSortBy,
  sortFieldLabel,
}: CuratedSectionContainerProps) => {
  const pressHandlers = useMemo(
    () =>
      players.reduce<Record<string, () => void>>(
        (acc, player) => ({
          ...acc,
          [player.id]: () => onPlayerPress(player),
        }),
        {}
      ),
    [players, onPlayerPress]
  );

  if (!loading && players.length === 0) return null;

  return (
    <CuratedSectionView
      title={title}
      players={players}
      loading={loading}
      pressHandlers={pressHandlers}
      renderAction={renderAction}
      effectiveSortBy={effectiveSortBy}
      sortFieldLabel={sortFieldLabel}
    />
  );
};

export default memo(CuratedSectionContainer);
